import React, { useState, useEffect } from "react";
import "../Styles/onboardingform.css";
import API_BASE_URL from "../config";

const STEPS = [
  { id: 1, label: "Organization Details", icon: "/on_form_01.png" },
  { id: 2, label: "Employee Strength Declaration", icon: "/on_form_02.png" },
  { id: 3, label: "Organization Contacts", icon: "/on_form_03.png" },
  { id: 4, label: "Other Details", icon: "/on_form_04.png" },
];

const INDUSTRY_OPTIONS = [
  "Agriculture",
  "Automotive",
  "Banking & Financial Services (BFSI)",
  "Chemicals & Petrochemicals",
  "Construction & Infrastructure",
  "Consumer Goods & FMCG",
  "Education & Training",
  "Energy & Utilities",
  "Engineering & Industrial Manufacturing",
  "Environmental Services",
  "Food & Beverage",
  "Government & Public Sector",
  "Healthcare & Pharmaceuticals",
  "Hospitality & Tourism",
  "Information Technology (IT) & Software",
  "Insurance",
  "Logistics & Supply Chain",
  "Media & Entertainment",
  "Mining & Metals",
  "Manufacturing & Production",
  "Non-Profit & NGOs",
  "Oil & Gas",
  "Real Estate",
  "Retail & E-commerce",
  "Telecommunications",
  "Textiles & Apparel",
  "Transportation",
  "Research & Development (R&D)",
  "Aerospace & Defense",
  "Consulting & Professional Services",
  "Others",
];

const STATE_OPTIONS = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const HEAR_ABOUT_OPTIONS = [
  "Search Engine / Google",
  "LinkedIn",
  "Social Media",
  "Email / Newsletter",
  "HR Community / Network",
  "Conference / HR Event",
  "Client / Organization Referral",
  "Direct Outreach",
  "Outstanding Workplaces™️ Website",
  "Others",
];

// Fields marked with * on each step
const REQUIRED_FIELDS = {
  1: [
    "legalEntityName",
    "industrySector",
    "yearsEstablished",
    "headOfficeAddress",
    "state",
    "city",
  ],
  2: ["permanentEmployees", "contractualEmployees"],
  3: [
    "coordinatorName",
    "coordinatorDesignation",
    "coordinatorEmail",
    "coordinatorMobile",
    "ceoName",
    "ceoDesignation",
    "ceoEmail",
    "ceoMobile",
    "hrHeadName",
    "hrHeadDesignation",
    "hrHeadEmail",
    "hrHeadMobile",
  ],
  4: ["gstinNumber", "pinCode", "certificationPurpose", "hearAboutUs"],
};

// The three contact persons on Step 3 must be different people
const CONTACT_GROUPS = [
  { prefix: "coordinator", label: "Coordinator" },
  { prefix: "ceo", label: "CEO / MD" },
  { prefix: "hrHead", label: "HR Head" },
];
// Fields that must not repeat across the three contacts
// (add { suffix: "Designation", label: "designation" } here to check designation too)
const DUPLICATE_CHECKS = [
  { suffix: "Name", label: "name" },
  { suffix: "Email", label: "email" },
  { suffix: "Mobile", label: "mobile number" },
];

const initialFormData = {
  legalEntityName: "",
  industrySector: "",
  yearsEstablished: "",
  orgDescription: "",
  headOfficeAddress: "",
  state: "",
  city: "",
  orgWebsite: "",

  permanentEmployees: "",
  contractualEmployees: "",

  coordinatorName: "",
  coordinatorDesignation: "",
  coordinatorEmail: "",
  coordinatorMobile: "",
  ceoName: "",
  ceoDesignation: "",
  ceoEmail: "",
  ceoMobile: "",
  hrHeadName: "",
  hrHeadDesignation: "",
  hrHeadEmail: "",
  hrHeadMobile: "",

  gstinNumber: "",
  pinCode: "",
  certificationPurpose: "",
  hearAboutUs: "",
  declarationAccuracy: false,
  declarationTerms: false,
};

export default function OnboardingForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [showDeclarationWarning, setShowDeclarationWarning] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Messages shown when the same contact details are entered twice
  const [duplicateMessages, setDuplicateMessages] = useState([]);

  // Every time the step changes (Next / Back / Submit success), open from the top
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStep, submitted]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (duplicateMessages.length > 0) {
      // user is correcting the duplicate details: clear the warning
      setDuplicateMessages([]);
      setErrors({});
    } else {
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }

    if (field === "declarationAccuracy" || field === "declarationTerms") {
      setShowDeclarationWarning(false);
    }
    if (submitError) setSubmitError("");
  };

  const totalEmployeeStrength =
    (Number(formData.permanentEmployees) || 0) +
    (Number(formData.contractualEmployees) || 0);

  // Normalise a value before comparing (case, spaces, +91 / 0 prefix on mobiles)
  const normalise = (suffix, value) => {
    let v = String(value ?? "").trim().toLowerCase();
    if (suffix === "Mobile") {
      v = v.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=\d{10}$)/, "");
    }
    return v;
  };

  // Finds the same name / email / mobile used by two different contacts
  const findDuplicates = () => {
    const dupErrors = {};
    const messages = [];
    DUPLICATE_CHECKS.forEach(({ suffix, label }) => {
      const seen = {};
      CONTACT_GROUPS.forEach(({ prefix, label: groupLabel }) => {
        const field = prefix + suffix;
        const v = normalise(suffix, formData[field]);
        if (!v) return;
        if (seen[v]) {
          dupErrors[field] = true;
          dupErrors[seen[v].field] = true;
          messages.push(
            `${seen[v].groupLabel} and ${groupLabel} have the same ${label}. Please enter different details.`
          );
        } else {
          seen[v] = { field, groupLabel };
        }
      });
    });
    return { dupErrors, messages };
  };

  // Returns true if every required field of the step is filled
  // (and, on Step 3, the three contacts are different people)
  const validateStep = (stepId) => {
    setDuplicateMessages([]);
    const missing = (REQUIRED_FIELDS[stepId] || []).filter(
      (field) => String(formData[field] ?? "").trim() === ""
    );
    const nextErrors = {};
    missing.forEach((field) => {
      nextErrors[field] = true;
    });
    setErrors(nextErrors);
    if (missing.length > 0) return false;

    if (stepId === 3) {
      const { dupErrors, messages } = findDuplicates();
      if (messages.length > 0) {
        setErrors(dupErrors);
        setDuplicateMessages(messages);
        return false;
      }
    }
    return true;
  };

  const fieldClass = (base, field) =>
    errors[field] ? `${base} ow-input--error` : base;

  const hasErrors = Object.keys(errors).length > 0;

  const requiredBanner = (
    <>
      {hasErrors && duplicateMessages.length === 0 && (
        <div className="ow-warning-banner">
          <img src="/on_form_05.png" alt="" className="ow-warning-icon" />
          <span>Please fill in all required fields to proceed.</span>
        </div>
      )}
      {duplicateMessages.length > 0 && (
        <div className="ow-warning-banner">
          <img src="/on_form_05.png" alt="" className="ow-warning-icon" />
          <span>
            {duplicateMessages.map((msg, i) => (
              <span key={i}>
                {msg}
                {i < duplicateMessages.length - 1 && <br />}
              </span>
            ))}
          </span>
        </div>
      )}
    </>
  );

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goBack = () => {
    setErrors({});
    setDuplicateMessages([]);
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      setShowDeclarationWarning(false);
      return;
    }
    if (!formData.declarationAccuracy || !formData.declarationTerms) {
      setShowDeclarationWarning(true);
      return;
    }
    setShowDeclarationWarning(false);
    setSubmitError("");
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to submit the form");
      setSubmitted(true); // shows "Form submitted successfully!"
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    setSubmitted(false);
    setCurrentStep(1);
    setFormData(initialFormData);
    setErrors({});
    setSubmitError("");
    setDuplicateMessages([]);
  };

  const getStepStatus = (stepId) => {
    if (submitted) return "completed";
    if (stepId < currentStep) return "completed";
    if (stepId === currentStep) return "current";
    return "upcoming";
  };

  return (
    <div className="ow-onboarding-page">
      {/* ---------- Top header (logo + title) ---------- */}
      <header className="ow-top-header">
        <div className="ow-top-logo">
          <img src="/New_logo.jpg" alt="Outstanding Workplaces" />
        </div>
        <div className="ow-top-text">
          <h1 className="ow-onboarding-title">
            Outstanding Workplaces&trade; Certification Application Form
          </h1>
          <p className="ow-onboarding-subtitle">
            Please fill in your details to complete the onboarding process
          </p>
        </div>
      </header>

      {/* ---------- Two info boxes ---------- */}
      <div className="ow-info-wrap">
        <div className="ow-info-box ow-info-box--dark">
          <div className="ow-info-title">
            Eligibility Requirements for the Outstanding Workplaces
            Certification Program – India
          </div>
          <ul className="ow-info-list">
            <li>
              Established Organisation: Minimum of 2 years of business
              operations.
            </li>
            <li>Employee Strength: At least 10 employees.</li>
            <li>
              Employee Engagement: Willingness to participate in a confidential
              employee survey to understand workplace experiences and
              organisational culture.
            </li>
          </ul>
        </div>

        <div className="ow-info-box ow-info-box--light">
          <div className="ow-info-title">For Program Details:</div>
          <ul className="ow-info-list">
            <li>
              Please connect with our representatives to learn more about the
              assessment process, investment plans, and reporting options
              available for your organisation.
            </li>
          </ul>
        </div>
      </div>

      {/* ---------- Steps ---------- */}
      <div className="ow-steps-row">
        {STEPS.map((step, idx) => {
          const status = getStepStatus(step.id);
          return (
            <React.Fragment key={step.id}>
              <div className="ow-step">
                <div className={`ow-step-circle ow-step-circle--${status}`}>
                  <img
                    src={step.icon}
                    alt={step.label}
                    className="ow-step-icon-img"
                  />
                </div>
                <div className={`ow-step-label ow-step-label--${status}`}>
                  {step.label}
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`ow-step-line ${
                    submitted || step.id < currentStep
                      ? "ow-step-line--active"
                      : ""
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ---------- Card ---------- */}
      <div className="ow-onboarding-card">
        {submitted ? (
          <div className="ow-success-wrap">
            <div className="ow-success-icon">
              <img
                src="/on_form_06.png"
                alt="Success"
                className="ow-success-check"
              />
            </div>
            <div className="ow-success-title">Form submitted successfully!</div>
            <div className="ow-success-subtitle">
              Thank you for completing your onboarding form.
            </div>
            <button className="ow-btn ow-btn-primary" onClick={handleBackToHome}>
              Back To Home
            </button>
          </div>
        ) : (
          <>
            {/* ================= STEP 1 ================= */}
            {currentStep === 1 && (
              <div className="ow-form-step">
                <div className="ow-form-group">
                  <label className="ow-label">
                    Organization Legal Entity Name{" "}
                    <span className="ow-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={fieldClass("ow-input", "legalEntityName")}
                    placeholder="Organization Legal Entity Name"
                    value={formData.legalEntityName}
                    onChange={handleChange("legalEntityName")}
                  />
                </div>

                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Industry / Sector <span className="ow-required">*</span>
                    </label>
                    <select
                      className={fieldClass("ow-input ow-select", "industrySector")}
                      value={formData.industrySector}
                      onChange={handleChange("industrySector")}
                    >
                      <option value="">Select sector</option>
                      {INDUSTRY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Years Established in India{" "}
                      <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "yearsEstablished")}
                      placeholder="Years Established in India"
                      value={formData.yearsEstablished}
                      onChange={handleChange("yearsEstablished")}
                    />
                  </div>
                </div>

                <div className="ow-form-group">
                  <label className="ow-label">Organization Description</label>
                  <textarea
                    className="ow-input ow-textarea"
                    placeholder="Brief summary of the primary operational focus or mandate..."
                    value={formData.orgDescription}
                    onChange={handleChange("orgDescription")}
                  />
                </div>

                <div className="ow-form-group">
                  <label className="ow-label">
                    Head Office Address <span className="ow-required">*</span>
                  </label>
                  <textarea
                    className={fieldClass(
                      "ow-input ow-textarea ow-textarea-sm",
                      "headOfficeAddress"
                    )}
                    value={formData.headOfficeAddress}
                    onChange={handleChange("headOfficeAddress")}
                  />
                </div>

                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      State / Union Territory{" "}
                      <span className="ow-required">*</span>
                    </label>
                    <select
                      className={fieldClass("ow-input ow-select", "state")}
                      value={formData.state}
                      onChange={handleChange("state")}
                    >
                      <option value="">Select state</option>
                      {STATE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      City <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "city")}
                      placeholder="City"
                      value={formData.city}
                      onChange={handleChange("city")}
                    />
                  </div>
                </div>

                <div className="ow-form-group">
                  <label className="ow-label">Organization Website</label>
                  <input
                    type="text"
                    className="ow-input"
                    placeholder="https://example.com"
                    value={formData.orgWebsite}
                    onChange={handleChange("orgWebsite")}
                  />
                </div>

                {requiredBanner}

                <div className="ow-btn-row">
                  <button className="ow-btn ow-btn-secondary" disabled>
                    Back to previous
                  </button>
                  <button className="ow-btn ow-btn-primary" onClick={goNext}>
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* ================= STEP 2 ================= */}
            {currentStep === 2 && (
              <div className="ow-form-step">
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Permanent Employees <span className="ow-required">*</span>
                    </label>
                    <input
                      type="number"
                      className={fieldClass("ow-input", "permanentEmployees")}
                      placeholder="0"
                      value={formData.permanentEmployees}
                      onChange={handleChange("permanentEmployees")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Contractual Employees{" "}
                      <span className="ow-required">*</span>
                    </label>
                    <input
                      type="number"
                      className={fieldClass("ow-input", "contractualEmployees")}
                      placeholder="0"
                      value={formData.contractualEmployees}
                      onChange={handleChange("contractualEmployees")}
                    />
                  </div>
                </div>

                <div className="ow-total-box">
                  <div className="ow-total-label">Total Employee Strength</div>
                  <div className="ow-total-value">{totalEmployeeStrength}</div>
                  <div className="ow-total-note">
                    Calculated automatically as permanent + contractual.
                  </div>
                </div>

                {requiredBanner}

                <div className="ow-btn-row">
                  <button className="ow-btn ow-btn-secondary" onClick={goBack}>
                    Back to previous
                  </button>
                  <button className="ow-btn ow-btn-primary" onClick={goNext}>
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* ================= STEP 3 ================= */}
            {currentStep === 3 && (
              <div className="ow-form-step">
                {/* ---------- Coordinator ---------- */}
                <div className="ow-section-title">Coordinator Details</div>
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Name <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "coordinatorName")}
                      placeholder="Full Name"
                      value={formData.coordinatorName}
                      onChange={handleChange("coordinatorName")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Designation <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "coordinatorDesignation")}
                      placeholder="Designation"
                      value={formData.coordinatorDesignation}
                      onChange={handleChange("coordinatorDesignation")}
                    />
                  </div>
                </div>
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Work Email ID <span className="ow-required">*</span>
                    </label>
                    <input
                      type="email"
                      className={fieldClass("ow-input", "coordinatorEmail")}
                      placeholder="Work Email Id"
                      value={formData.coordinatorEmail}
                      onChange={handleChange("coordinatorEmail")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Mobile Number <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "coordinatorMobile")}
                      placeholder="Mobile Number"
                      value={formData.coordinatorMobile}
                      onChange={handleChange("coordinatorMobile")}
                    />
                  </div>
                </div>

                <hr className="ow-divider" />

                {/* ---------- CEO ---------- */}
                <div className="ow-section-title">
                  CEO / MD / Head of Organization / Country Head
                </div>

                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Name <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "ceoName")}
                      placeholder="Full Name"
                      value={formData.ceoName}
                      onChange={handleChange("ceoName")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Designation <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "ceoDesignation")}
                      placeholder="Designation"
                      value={formData.ceoDesignation}
                      onChange={handleChange("ceoDesignation")}
                    />
                  </div>
                </div>
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Work Email ID <span className="ow-required">*</span>
                    </label>
                    <input
                      type="email"
                      className={fieldClass("ow-input", "ceoEmail")}
                      placeholder="Email Id"
                      value={formData.ceoEmail}
                      onChange={handleChange("ceoEmail")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Mobile Number <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "ceoMobile")}
                      placeholder="Mobile Number"
                      value={formData.ceoMobile}
                      onChange={handleChange("ceoMobile")}
                    />
                  </div>
                </div>

                <hr className="ow-divider" />

                {/* ---------- HR Head ---------- */}
                <div className="ow-section-title">HR Head Details</div>

                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Name <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "hrHeadName")}
                      placeholder="Full Name"
                      value={formData.hrHeadName}
                      onChange={handleChange("hrHeadName")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Designation <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "hrHeadDesignation")}
                      placeholder="Designation"
                      value={formData.hrHeadDesignation}
                      onChange={handleChange("hrHeadDesignation")}
                    />
                  </div>
                </div>
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Work Email ID <span className="ow-required">*</span>
                    </label>
                    <input
                      type="email"
                      className={fieldClass("ow-input", "hrHeadEmail")}
                      placeholder="Email Id"
                      value={formData.hrHeadEmail}
                      onChange={handleChange("hrHeadEmail")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      Mobile Number <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "hrHeadMobile")}
                      placeholder="Mobile Number"
                      value={formData.hrHeadMobile}
                      onChange={handleChange("hrHeadMobile")}
                    />
                  </div>
                </div>

                {requiredBanner}

                <div className="ow-btn-row">
                  <button className="ow-btn ow-btn-secondary" onClick={goBack}>
                    Back to previous
                  </button>
                  <button className="ow-btn ow-btn-primary" onClick={goNext}>
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* ================= STEP 4 ================= */}
            {currentStep === 4 && (
              <div className="ow-form-step">
                <div className="ow-section-title">GSTIN Information</div>
                <div className="ow-form-row">
                  <div className="ow-form-group">
                    <label className="ow-label">
                      GSTIN Number <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "gstinNumber")}
                      placeholder="GSTIN Number"
                      value={formData.gstinNumber}
                      onChange={handleChange("gstinNumber")}
                    />
                  </div>
                  <div className="ow-form-group">
                    <label className="ow-label">
                      PIN Code <span className="ow-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={fieldClass("ow-input", "pinCode")}
                      placeholder="PIN Code"
                      value={formData.pinCode}
                      onChange={handleChange("pinCode")}
                    />
                  </div>
                </div>

                <hr className="ow-divider" />

                <div className="ow-section-title ow-section-title-spaced">
                  Certification Details
                </div>
                <div className="ow-form-group">
                  <label className="ow-label">
                    Certification Purpose / Objective{" "}
                    <span className="ow-required">*</span>
                  </label>
                  <textarea
                    className={fieldClass(
                      "ow-input ow-textarea",
                      "certificationPurpose"
                    )}
                    placeholder="Describe the organizational goal..."
                    value={formData.certificationPurpose}
                    onChange={handleChange("certificationPurpose")}
                  />
                </div>

                <div className="ow-form-group">
                  <label className="ow-label">
                    How did you hear about us?{" "}
                    <span className="ow-required">*</span>
                  </label>
                  <select
                    className={fieldClass("ow-input ow-select", "hearAboutUs")}
                    value={formData.hearAboutUs}
                    onChange={handleChange("hearAboutUs")}
                  >
                    <option value="">Select Option</option>
                    {HEAR_ABOUT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="ow-checkbox-row">
                  <input
                    type="checkbox"
                    checked={formData.declarationAccuracy}
                    onChange={handleChange("declarationAccuracy")}
                  />
                  <span>
                    I confirm that all information provided in this application
                    is accurate and complete to the best of my knowledge.
                  </span>
                </label>

                <label className="ow-checkbox-row">
                  <input
                    type="checkbox"
                    checked={formData.declarationTerms}
                    onChange={handleChange("declarationTerms")}
                  />
                  <span>
                    I agree to the terms and conditions, and consent to the
                    processing of this application as per the organization's
                    process.
                  </span>
                </label>

                {requiredBanner}

                {showDeclarationWarning && (
                  <div className="ow-warning-banner">
                    <img
                      src="/on_form_05.png"
                      alt=""
                      className="ow-warning-icon"
                    />
                    <span>Please accept both declarations to proceed.</span>
                  </div>
                )}

                {submitError && (
                  <div className="ow-warning-banner">
                    <img
                      src="/on_form_05.png"
                      alt=""
                      className="ow-warning-icon"
                    />
                    <span>{submitError}</span>
                  </div>
                )}

                <div className="ow-btn-row">
                  <button className="ow-btn ow-btn-secondary" onClick={goBack}>
                    Back to previous
                  </button>
                  <button
                    className="ow-btn ow-btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}